import React, { useState } from 'react';
import {
  Squares2X2Icon, ExclamationTriangleIcon, ArrowPathIcon,
  AdjustmentsHorizontalIcon, CheckCircleIcon, CubeIcon
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, usePagination, useDebounce } from '../../hooks';
import { adminAPI } from '../../services/api';
import {
  Card, SectionHeader, SearchInput, Button, Badge, StatusBadge,
  Modal, Input, Select, Pagination, LoadingPage, EmptyState,
  HighRiskActionModal
} from '../../components/common/UI';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export default function InventoryGovernance() {
  const [activeTab, setActiveTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustQty, setAdjustQty] = useState(0);
  const [adjustType, setAdjustType] = useState('add');
  const debouncedSearch = useDebounce(search, 300);
  const { page, setPage } = usePagination();

  const { data, loading, refetch } = useQuery(
    () => adminAPI.inventoryOverview({ page, limit: 15, search: debouncedSearch, filter: activeTab }),
    [page, debouncedSearch, activeTab]
  );

  const { data: historyData, loading: historyLoading, refetch: refetchHistory } = useQuery(
    () => adminAPI.inventoryHistory({ page, limit: 15 }),
    [page, activeTab],
    { enabled: activeTab === 'history' }
  );

  const [highRiskLoading, setHighRiskLoading] = useState(false);

  const handleConfirmAdjustment = async ({ reason, idempotencyKey }) => {
    if (!selectedItem) return;
    setHighRiskLoading(true);
    try {
      const calculatedQty = adjustType === 'subtract' ? -Math.abs(Number(adjustQty)) : Math.abs(Number(adjustQty));
      await adminAPI.inventoryAdjust(
        {
          product_id: selectedItem.id,
          variant_id: selectedItem.variant_id || null,
          adjustment_qty: calculatedQty,
          reason,
        },
        {
          headers: { 'Idempotency-Key': idempotencyKey }
        }
      );
      toast.success('Inventory adjustment recorded in audit log');
      setAdjustModalOpen(false);
      setSelectedItem(null);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.response?.data?.message || 'Failed to adjust inventory');
    } finally {
      setHighRiskLoading(false);
    }
  };

  const rows = data?.rows || data?.items || [];
  const total = data?.total || data?.count || 0;

  return (
    <div className="space-y-6 animate-in">
      <SectionHeader
        title="Inventory & Warehouse Governance"
        subtitle="Platform-wide multi-seller inventory monitor and audit-gated stock adjustments"
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-white border border-surface-200">
          <p className="text-xs font-semibold text-surface-500 uppercase">Total Tracked SKUs</p>
          <p className="text-2xl font-bold text-surface-900 mt-1">{total}</p>
        </Card>
        <Card className="p-4 bg-white border border-surface-200">
          <p className="text-xs font-semibold text-warning-600 uppercase">Low Stock SKUs</p>
          <p className="text-2xl font-bold text-warning-700 mt-1">{data?.low_stock_count || 0}</p>
        </Card>
        <Card className="p-4 bg-white border border-surface-200">
          <p className="text-xs font-semibold text-danger-600 uppercase">Out of Stock</p>
          <p className="text-2xl font-bold text-danger-700 mt-1">{data?.out_of_stock_count || 0}</p>
        </Card>
        <Card className="p-4 bg-white border border-surface-200">
          <p className="text-xs font-semibold text-brand-600 uppercase">Recent Adjustments</p>
          <p className="text-2xl font-bold text-brand-700 mt-1">{data?.recent_adjustments || 0}</p>
        </Card>
      </div>

      {/* Tabs & Search */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex gap-1 rounded-xl bg-surface-100 p-1 w-full sm:w-auto">
          {[
            { key: 'overview', label: 'All Inventory' },
            { key: 'low_stock', label: 'Low Stock (< 10)' },
            { key: 'out_of_stock', label: 'Out of Stock' },
            { key: 'history', label: 'Stock Movement Audit' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                activeTab === tab.key ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab !== 'history' && (
          <SearchInput
            value={search}
            onChange={(val) => { setSearch(val); setPage(1); }}
            placeholder="Search by product name, SKU or seller..."
            className="w-full sm:w-72"
          />
        )}
      </div>

      {/* Main Table */}
      {activeTab !== 'history' ? (
        <Card className="p-0 overflow-hidden">
          {loading ? (
            <LoadingPage />
          ) : rows.length === 0 ? (
            <EmptyState icon={Squares2X2Icon} title="No inventory items found" description="No products match your filter criteria." />
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product / SKU</th>
                    <th>Seller</th>
                    <th>Category</th>
                    <th>Stock Level</th>
                    <th>Status</th>
                    <th>Last Updated</th>
                    <th className="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => {
                    const isLow = item.stock > 0 && item.stock <= 10;
                    const isOut = item.stock <= 0;
                    return (
                      <tr key={item.id}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-surface-100 border border-surface-200 flex items-center justify-center flex-shrink-0">
                              {item.image_url ? (
                                <img src={item.image_url} alt="" className="h-full w-full object-cover rounded-lg" />
                              ) : (
                                <CubeIcon className="h-5 w-5 text-surface-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-surface-900">{item.name || item.product_name}</p>
                              <p className="text-xs font-mono text-surface-400">SKU: {item.sku || `PRD-${item.id}`}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <p className="text-xs font-medium text-surface-800">{item.business_name || item.seller_name || 'Direct / Platform'}</p>
                        </td>
                        <td>
                          <span className="text-xs text-surface-600">{item.category_name || 'General'}</span>
                        </td>
                        <td>
                          <span className={`text-sm font-bold ${isOut ? 'text-danger-600' : isLow ? 'text-warning-600' : 'text-surface-900'}`}>
                            {item.stock} units
                          </span>
                        </td>
                        <td>
                          <Badge variant={isOut ? 'red' : isLow ? 'yellow' : 'green'}>
                            {isOut ? 'Out of Stock' : isLow ? 'Low Stock' : 'In Stock'}
                          </Badge>
                        </td>
                        <td>
                          <p className="text-xs text-surface-500">
                            {item.updated_at ? format(new Date(item.updated_at), 'dd MMM yyyy, HH:mm') : '—'}
                          </p>
                        </td>
                        <td className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={AdjustmentsHorizontalIcon}
                            onClick={() => {
                              setSelectedItem(item);
                              setAdjustQty(1);
                              setAdjustType('add');
                              setAdjustModalOpen(true);
                            }}
                          >
                            Adjust
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-4 border-t border-surface-100">
            <Pagination page={page} pages={Math.ceil(total / 15) || 1} onPageChange={setPage} />
          </div>
        </Card>
      ) : (
        /* History Audit Tab */
        <Card className="p-0 overflow-hidden">
          {historyLoading ? (
            <LoadingPage />
          ) : (historyData?.rows || []).length === 0 ? (
            <EmptyState icon={ArrowPathIcon} title="No stock movement history" description="Recent inventory adjustments and movement will appear here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Product</th>
                    <th>Adjustment</th>
                    <th>Actor / Admin</th>
                    <th>Mandatory Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(historyData?.rows || []).map((h) => (
                    <tr key={h.id}>
                      <td className="text-xs text-surface-500">
                        {h.created_at ? format(new Date(h.created_at), 'dd MMM yyyy, HH:mm') : '—'}
                      </td>
                      <td className="font-semibold text-xs text-surface-800">{h.product_name}</td>
                      <td>
                        <span className={`text-xs font-bold ${h.adjustment_qty > 0 ? 'text-success-600' : 'text-danger-600'}`}>
                          {h.adjustment_qty > 0 ? `+${h.adjustment_qty}` : h.adjustment_qty}
                        </span>
                      </td>
                      <td className="text-xs text-surface-700">{h.admin_email || `Admin #${h.admin_id}`}</td>
                      <td className="text-xs text-surface-600 italic">"{h.reason}"</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* High-Risk Adjustment Modal */}
      {selectedItem && (
        <HighRiskActionModal
          open={adjustModalOpen}
          onClose={() => { setAdjustModalOpen(false); setSelectedItem(null); }}
          onConfirm={handleConfirmAdjustment}
          title="Stock Level Manual Adjustment"
          danger={false}
          confirmLabel="Commit Stock Adjustment"
          warningMessage="Manual inventory override alters live stock levels for customer checkout and will be recorded in the audit log."
          entitySummary={
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium text-surface-900">{selectedItem.name || selectedItem.product_name}</span>
                <span className="font-bold text-surface-700">Current Stock: {selectedItem.stock}</span>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Select
                  value={adjustType}
                  onChange={(e) => setAdjustType(e.target.value)}
                  className="w-32 text-xs"
                >
                  <option value="add">Add (+)</option>
                  <option value="subtract">Deduct (-)</option>
                </Select>
                <Input
                  type="number"
                  min="1"
                  value={adjustQty}
                  onChange={(e) => setAdjustQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-28 text-xs font-bold"
                />
                <span className="text-xs text-surface-500">
                  Resulting Stock:{' '}
                  <strong className="text-surface-900">
                    {adjustType === 'add'
                      ? Number(selectedItem.stock) + Number(adjustQty)
                      : Math.max(0, Number(selectedItem.stock) - Number(adjustQty))}
                  </strong>
                </span>
              </div>
            </div>
          }
          loading={highRiskLoading}
        />
      )}
    </div>
  );
}
